import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { z } from 'zod';

import { GenericFormScreen } from '@/components/shared/GenericFormScreen/GenericFormScreen';
import { Button } from '@/components/ui/Button/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/Form/Form';
import { Input } from '@/components/ui/Input/Input';
import { toast } from '@/components/ui/Toast/Toast';
import { paths } from '@/config/paths';
import { useInitializeForgottenUsername } from '@/features/auth/api/initializeForgottenUsername';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';

export const InitializeStep = ({ goToNextStep }: { goToNextStep: () => void }) => {
  const { t } = useTranslation('auth', { keyPrefix: 'forgotUsername.steps.initialize' });
  const { t: tValidation } = useTranslation('utils', { keyPrefix: 'validation' });
  const { translateError } = useErrorTranslation();

  const formSchema = z.object({
    email: z
      .string()
      .email({ message: tValidation('emailWrongFormat') })
      .min(1, { message: tValidation('required') }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
    },
  });

  const email = form.watch('email');

  const { mutate: initializeForgottenUsername, isPending } = useInitializeForgottenUsername({
    email,
    options: {
      onSuccess: () => {
        goToNextStep();
      },
      onError: (error) => {
        toast({
          variant: 'destructive',
          description: translateError(error),
        });
      },
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    initializeForgottenUsername(values);
  };

  return (
    <GenericFormScreen
      title={t('title')}
      description={t('description')}
      leftActionElement={
        <Button type='button' variant='link' asChild>
          <Link to={paths.auth.login.path}>{t('back.label')}</Link>
        </Button>
      }
      rightActionElement={
        <Button type='submit' form='forgot-username-initialize-form' isLoading={isPending} disabled={isPending}>
          {t('submit.label')}
        </Button>
      }
    >
      <Form {...form}>
        <form
          id='forgot-username-initialize-form'
          onSubmit={(e) => {
            void form.handleSubmit(onSubmit)(e);
          }}
          className='flex flex-col gap-4'
        >
          <FormField
            control={form.control}
            name='email'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('email.label')}</FormLabel>
                <FormControl>
                  <Input {...field} autoComplete='email' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </GenericFormScreen>
  );
};
